/**
 * MA-ARCH-181-WR-BANNER: War Room Attribution Coverage Banner
 * 
 * Compact, always-visible trust signal for the War Room.
 * Reflects current War Room filter context (period, campaign, workspace).
 * 
 * Props:
 *   period      — "7d" | "30d" | "mtd" | "ytd"
 *   campaignId  — optional canonical campaign_id
 *   workspaceId — optional workspace_id
 */

import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ShieldCheck, ShieldAlert, ShieldQuestion, ChevronRight } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

const WEAK_COVERAGE_THRESHOLD = 40;

function periodToDateRange(period) {
  const now = new Date();
  let dateFrom;
  const dateTo = now.toISOString().slice(0, 10);

  switch (period) {
    case "7d": {
      const d = new Date(now);
      d.setDate(d.getDate() - 7);
      dateFrom = d.toISOString().slice(0, 10);
      break;
    }
    case "30d": {
      const d = new Date(now);
      d.setDate(d.getDate() - 30);
      dateFrom = d.toISOString().slice(0, 10);
      break;
    }
    case "mtd": {
      dateFrom = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01`;
      break;
    }
    case "ytd": {
      dateFrom = `${now.getFullYear()}-01-01`;
      break;
    }
    default:
      dateFrom = null;
  }
  return { dateFrom, dateTo };
}

function periodLabel(period) {
  switch (period) {
    case "7d": return "Last 7 days";
    case "30d": return "Last 30 days";
    case "mtd": return "Month to date";
    case "ytd": return "Year to date";
    default: return "";
  }
}

function CoverageSegment({ icon: Icon, label, pct, count, colorClass, muted }) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className={cn("flex items-center gap-1.5 cursor-default", muted && "opacity-50")}>
            <Icon className={cn("h-3 w-3", colorClass)} />
            <span className="text-xs text-slate-400">{label}</span>
            <span className="text-xs font-semibold text-slate-200">{pct}%</span>
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="bg-slate-800 border-slate-700">
          <p className="text-xs">{count} lead{count !== 1 ? "s" : ""} with {label} attribution</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default function WarRoomCoverageBanner({ period, campaignId, workspaceId }) {
  const navigate = useNavigate();
  const { dateFrom, dateTo } = useMemo(() => periodToDateRange(period), [period]);

  const { data, isLoading } = useQuery({
    queryKey: ["wr-attribution-coverage", dateFrom, dateTo, campaignId, workspaceId],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (dateFrom) params.append("date_from", dateFrom);
      if (dateTo) params.append("date_to", dateTo);
      if (campaignId && campaignId !== "all") params.append("campaign_id", campaignId);
      if (workspaceId) params.append("workspace_id", workspaceId);
      
      try {
        const res = await api.get(`/attribution/coverage?${params}`);
        return res.data;
      } catch (error) {
        // MA-PHASE11A-FINAL-CONSOLE-CLEANUP-022: Silent fail for permission-denied
        if (error.response?.status === 403 || error.response?.status === 404) {
          return null;
        }
        throw error;
      }
    },
    staleTime: 60000,
    refetchOnWindowFocus: false,
    retry: false,
  });

  // Loading state — minimal shimmer
  if (isLoading) {
    return (
      <div
        className="flex items-center gap-4 px-4 py-2.5 rounded-lg bg-slate-800/20 border border-slate-700/15 animate-pulse"
        data-testid="wr-coverage-banner-loading"
      >
        <div className="h-3 w-32 bg-slate-700/40 rounded" />
        <div className="h-3 w-16 bg-slate-700/40 rounded" />
        <div className="h-3 w-16 bg-slate-700/40 rounded" />
      </div>
    );
  }

  // No data
  if (!data || data.total === 0) {
    return (
      <div
        className="flex items-center gap-3 px-4 py-2.5 rounded-lg bg-slate-800/20 border border-slate-700/15"
        data-testid="wr-coverage-banner-empty"
      >
        <ShieldQuestion className="h-3.5 w-3.5 text-slate-600" />
        <span className="text-xs text-slate-500">
          No attribution data available for current filters
        </span>
      </div>
    );
  }

  const isWeak = data.deterministic_pct < WEAK_COVERAGE_THRESHOLD;
  const isLowVolume = data.total < 5;

  // Build context label
  const contextParts = [];
  contextParts.push(periodLabel(period));
  if (data.scope === "campaign") contextParts.push("filtered campaign");
  if (!campaignId || campaignId === "all") {
    if (!workspaceId) contextParts.push("all leads");
  }
  const contextText = contextParts.join(" · ");

  const handleDrillDown = () => {
    const params = new URLSearchParams();
    if (dateFrom) params.set("lead_created_from", dateFrom);
    if (dateTo) params.set("lead_created_to", dateTo);
    navigate(`/app/leads?${params.toString()}`);
  };

  return (
    <div
      className="flex flex-wrap items-center gap-x-5 gap-y-2 px-4 py-2.5 rounded-lg bg-slate-800/20 border border-slate-700/15"
      data-testid="wr-coverage-banner"
    >
      {/* Title + context */}
      <div className="flex items-center gap-2">
        <ShieldCheck className="h-3.5 w-3.5 text-slate-500" />
        <span className="text-xs font-medium text-slate-400">Attribution Coverage</span>
        <span className="text-[10px] text-slate-600 hidden sm:inline">
          {contextText}
        </span>
      </div>

      {/* Segmented bar — compact inline */}
      {data.total > 0 && (
        <div className="flex items-center gap-0.5 h-1.5 w-20 rounded-full overflow-hidden bg-slate-800">
          {data.deterministic_pct > 0 && (
            <div className="h-full bg-emerald-500/70" style={{ width: `${data.deterministic_pct}%` }} />
          )}
          {data.inferred_pct > 0 && (
            <div className="h-full bg-amber-500/70" style={{ width: `${data.inferred_pct}%` }} />
          )}
          {data.unknown_pct > 0 && (
            <div className="h-full bg-slate-600" style={{ width: `${data.unknown_pct}%` }} />
          )}
        </div>
      )}

      {/* Metrics */}
      <div className="flex items-center gap-4">
        <CoverageSegment
          icon={ShieldCheck}
          label="High"
          pct={data.deterministic_pct}
          count={data.deterministic_count}
          colorClass="text-emerald-400"
        />
        <CoverageSegment
          icon={ShieldAlert}
          label="Medium"
          pct={data.inferred_pct}
          count={data.inferred_count}
          colorClass="text-amber-400"
          muted={data.inferred_count === 0}
        />
        <CoverageSegment
          icon={ShieldQuestion}
          label="Unknown"
          pct={data.unknown_pct}
          count={data.unknown_count}
          colorClass="text-slate-500"
          muted={data.unknown_count === 0}
        />
      </div>

      {/* Total (secondary) */}
      <span className="text-[10px] text-slate-600">
        {data.total} lead{data.total !== 1 ? "s" : ""}
        {isLowVolume && " · low data"}
      </span>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Drill-down link */}
      <button
        onClick={handleDrillDown}
        className="flex items-center gap-1 text-[11px] text-slate-500 hover:text-slate-300 transition-colors"
        data-testid="wr-coverage-drilldown"
      >
        View Leads
        <ChevronRight className="h-3 w-3" />
      </button>

      {/* Weak coverage guidance — inline, below content */}
      {isWeak && !isLowVolume && (
        <div
          className="w-full text-[11px] text-amber-400/60 leading-relaxed pt-1 border-t border-slate-700/10"
          data-testid="wr-coverage-nudge"
        >
          {data.deterministic_pct < 10
            ? "Most leads have low attribution confidence. Route traffic through MissionAssist landing pages for stronger attribution."
            : "Some leads have low attribution confidence. Using connected landing pages and funnels improves accuracy."}
        </div>
      )}
    </div>
  );
}
